import { Empty, notification, Tag } from "antd";
import { get } from "lodash";
import { useState } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useMutation, useQuery } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import cuid from "cuid";
import StepCard from "./StepCard";
import StepEditorPanel from "./StepEditorPanel";
import StepTypePicker from "./StepTypePicker";

const SortableStep = ({ step, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: step.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <div {...listeners} style={{ cursor: "grab" }}>
        {children}
      </div>
    </div>
  );
};

const ConnectorLine = () => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
    <svg width="2" height="20" style={{ display: "block" }}>
      <line x1="1" y1="0" x2="1" y2="20" stroke="#d1d5db" strokeWidth="2" />
    </svg>
  </div>
);

const SequenceBuilder = ({ sequenceId, campaignState }) => {
  const readOnly = campaignState !== "draft";
  const [selectedStepId, setSelectedStepId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const { data, loading, refetch } = useQuery(graphql.queries.CRM_SEQUENCE, {
    variables: { where: { id: sequenceId } },
    skip: !sequenceId,
    fetchPolicy: "network-only",
  });

  const [addStep] = useMutation(graphql.mutations.CREATE_CRM_STEP);
  const [updateStep] = useMutation(graphql.mutations.UPDATE_CRM_STEP);
  const [deleteStep] = useMutation(graphql.mutations.DELETE_CRM_STEP);

  const sequence = get(data, "crmSequence");
  const steps = get(sequence, "steps") || [];
  const rootSteps = steps
    .filter((s) => !s.parentId)
    .sort((a, b) => a.order - b.order);

  const selectedStep = steps.find((s) => s.id === selectedStepId);

  const handleAddStep = async (type, parentId, branchKey, afterOrder) => {
    const id = cuid();
    const order = afterOrder != null ? afterOrder + 1 : rootSteps.length;
    const defaultConfig = type === "delay"
      ? { days: 2, type: "sending_days" }
      : type === "condition"
        ? { conditionType: "email_opened", timeframeDays: 3, waitMode: "within" }
        : { label: "" };

    try {
      await addStep({
        variables: {
          data: {
            id,
            type,
            order,
            parentId: parentId || null,
            branchKey: branchKey || null,
            config: defaultConfig,
            isAutomatic: type !== "task" && type !== "call",
            sequence: { id: sequenceId },
          },
        },
      });
      await refetch();
      setSelectedStepId(id);
      notification.success({ message: "Étape ajoutée" });
    } catch (err) {
      notification.error({ message: "Échec de l'ajout de l'étape" });
    }
  };

  const handleUpdateStep = async (updates) => {
    if (!selectedStep || readOnly) return;
    try {
      await updateStep({
        variables: {
          where: { id: selectedStep.id },
          data: updates,
        },
      });
      await refetch();
    } catch (err) {
      notification.error({ message: "Échec de la mise à jour" });
    }
  };

  const handleDeleteStep = async (stepId) => {
    try {
      await deleteStep({ variables: { where: { id: stepId } } });
      if (selectedStepId === stepId) setSelectedStepId(null);
      await refetch();
      notification.success({ message: "Étape supprimée" });
    } catch (err) {
      notification.error({ message: "Échec de la suppression" });
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id || readOnly) return;

    const oldIndex = rootSteps.findIndex((s) => s.id === active.id);
    const newIndex = rootSteps.findIndex((s) => s.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    try {
      await updateStep({
        variables: {
          where: { id: active.id },
          data: { order: newIndex },
        },
      });

      const stepsToReorder = [...rootSteps];
      const [moved] = stepsToReorder.splice(oldIndex, 1);
      stepsToReorder.splice(newIndex, 0, moved);

      for (let i = 0; i < stepsToReorder.length; i++) {
        if (stepsToReorder[i].id !== active.id && stepsToReorder[i].order !== i) {
          await updateStep({
            variables: {
              where: { id: stepsToReorder[i].id },
              data: { order: i },
            },
          });
        }
      }
      await refetch();
    } catch (err) {
      notification.error({ message: "Échec du réordonnancement" });
    }
  };

  const renderBranches = (step) => {
    const yesBranch = steps
      .filter((s) => s.parentId === step.id && s.branchKey === "yes")
      .sort((a, b) => a.order - b.order);
    const noBranch = steps
      .filter((s) => s.parentId === step.id && s.branchKey === "no")
      .sort((a, b) => a.order - b.order);

    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <StepCard
          step={step}
          selected={selectedStepId === step.id}
          onClick={() => setSelectedStepId(step.id)}
          onDelete={() => handleDeleteStep(step.id)}
          readOnly={readOnly}
        />
        <ConnectorLine />
        <svg width="200" height="30" style={{ display: "block" }}>
          <line x1="100" y1="0" x2="40" y2="30" stroke="#d1d5db" strokeWidth="2" />
          <line x1="100" y1="0" x2="160" y2="30" stroke="#d1d5db" strokeWidth="2" />
        </svg>
        <div className="d-flex" style={{ gap: "32px" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 180 }}>
            <Tag color="green" style={{ marginBottom: 8 }}>✅ Oui ({yesBranch.length})</Tag>
            {yesBranch.map((s) => (
              <div key={s.id} style={{ marginBottom: 8 }}>
                <StepCard
                  step={s}
                  selected={selectedStepId === s.id}
                  onClick={() => setSelectedStepId(s.id)}
                  onDelete={() => handleDeleteStep(s.id)}
                  readOnly={readOnly}
                />
              </div>
            ))}
            {!readOnly && (
              <StepTypePicker
                onSelect={(type) => handleAddStep(type, step.id, "yes", yesBranch.length)}
                disabled={readOnly}
              />
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 180 }}>
            <Tag color="red" style={{ marginBottom: 8 }}>❌ Non ({noBranch.length})</Tag>
            {noBranch.map((s) => (
              <div key={s.id} style={{ marginBottom: 8 }}>
                <StepCard
                  step={s}
                  selected={selectedStepId === s.id}
                  onClick={() => setSelectedStepId(s.id)}
                  onDelete={() => handleDeleteStep(s.id)}
                  readOnly={readOnly}
                />
              </div>
            ))}
            {!readOnly && (
              <StepTypePicker
                onSelect={(type) => handleAddStep(type, step.id, "no", noBranch.length)}
                disabled={readOnly}
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  if (!sequenceId) {
    return <Empty description="Aucune séquence associée à cette campagne" />;
  }

  if (loading) return <div style={{ textAlign: "center", padding: 40 }}>Chargement...</div>;

  return (
    <div className="sequence-builder">
      {readOnly && (
        <div style={{
          padding: "8px 16px",
          background: "#fff8ee",
          border: "1px solid #f2994a",
          borderRadius: 8,
          marginBottom: 16,
          fontSize: 13,
          color: "#92400e",
        }}>
          La séquence est verrouillée. Dupliquez la campagne pour la modifier.
        </div>
      )}

      <div style={{
        display: "flex",
        justifyContent: "center",
        padding: "12px",
        background: "#f5f7fa",
        borderRadius: 8,
        marginBottom: 16,
        fontSize: 12,
        color: "#6b7280",
        gap: "16px",
      }}>
        <span><strong>{rootSteps.length}</strong> étapes</span>
        <span>·</span>
        <span>
          {steps.filter((s) => s.type === "email").length > 0 && "📧 "}
          {steps.filter((s) => s.type.startsWith("linkedin")).length > 0 && "🔗 "}
          {steps.filter((s) => s.type === "call").length > 0 && "📞 "}
          {steps.filter((s) => s.type === "whatsapp").length > 0 && "💬 "}
          {steps.filter((s) => s.type === "task").length > 0 && "✅ "}
          {steps.filter((s) => s.type === "api").length > 0 && "🔌 "}
        </span>
        <span>·</span>
        <span>
          ~{steps.filter((s) => s.type === "delay").reduce((sum, s) => sum + (get(s, "config.days", 0) || 0), 0)} jours
        </span>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={rootSteps.map((s) => s.id)} strategy={verticalListSortingStrategy} disabled={readOnly}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0px", padding: "20px 0" }}>
            {rootSteps.length === 0 ? (
              <Empty description="Séquence vide. Ajoutez votre première étape." image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              rootSteps.map((step, i) => (
                <div key={step.id} style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  {step.type === "condition" ? (
                    renderBranches(step)
                  ) : (
                    <SortableStep step={step}>
                      <StepCard
                        step={step}
                        selected={selectedStepId === step.id}
                        onClick={() => setSelectedStepId(step.id)}
                        onDelete={() => handleDeleteStep(step.id)}
                        readOnly={readOnly}
                      />
                    </SortableStep>
                  )}
                  {i < rootSteps.length - 1 && (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                      <ConnectorLine />
                      {!readOnly && (
                        <StepTypePicker
                          onSelect={(type) => handleAddStep(type, null, null, step.order)}
                          disabled={readOnly}
                        />
                      )}
                      <ConnectorLine />
                    </div>
                  )}
                </div>
              ))
            )}

            {!readOnly && (
              <div style={{ marginTop: 12 }}>
                <ConnectorLine />
                <StepTypePicker
                  onSelect={(type) => handleAddStep(type, null, null, rootSteps.length)}
                  disabled={readOnly}
                />
              </div>
            )}
          </div>
        </SortableContext>
      </DndContext>

      <StepEditorPanel
        step={selectedStep}
        open={!!selectedStep}
        onClose={() => setSelectedStepId(null)}
        onUpdate={handleUpdateStep}
        readOnly={readOnly}
      />
    </div>
  );
};

export default SequenceBuilder;
